import { useEffect, useRef, useState, useCallback, Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./driver-map.css";
import { Star, Car, Bike, Truck, MapPin, X, RefreshCw, Clock, MessageCircle, Send, User } from "lucide-react";

class MapErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[DriverMap] render error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-white/90 rounded-2xl z-[600]">
          <div className="text-center">
            <p className="font-semibold text-slate-700 text-sm">Could not load driver details</p>
            <button
              onClick={() => this.setState({ hasError: false })}
              className="mt-2 text-xs text-primary underline"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

type DriverPin = {
  userId: number;
  name: string | null;
  avatarUrl: string | null;
  vehicleType: string;
  lastLat: number;
  lastLng: number;
  lastLocationAt: string | null;
  avgRating: number | null;
  ratingCount: number;
  bio: string | null;
  serviceTowns: string[];
};

type DriverProfile = DriverPin & {
  town: string | null;
  recentReviews: { rating: number; comment: string | null; createdAt: string }[];
};

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function getMyUserId(): number | null {
  const token = localStorage.getItem("ayrshire_token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload as { userId?: number }).userId ?? null;
  } catch { return null; }
}

function vehicleEmoji(v: string) {
  if (v === "van") return "🚐";
  if (v === "car") return "🚗";
  return "🚲";
}

function vehicleColor(v: string) {
  if (v === "van") return "#7c3aed";
  if (v === "car") return "#1d4ed8";
  return "#059669";
}

function makeDriverIcon(vehicleType: string, selected: boolean) {
  const color = vehicleColor(vehicleType);
  const emoji = vehicleEmoji(vehicleType);
  const size = selected ? 48 : 40;
  const border = selected ? `3px solid ${color}` : "2px solid #fff";
  const shadow = selected ? "0 0 0 3px rgba(30,58,95,0.25), 0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.25)";
  return L.divIcon({
    className: "",
    html: `<div style="
      width:${size}px;height:${size}px;
      background:${color};
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      border:${border};
      box-shadow:${shadow};
      display:flex;align-items:center;justify-content:center;
      transition:all 0.15s ease;
    "><span style="transform:rotate(45deg);font-size:${selected ? 22 : 18}px;line-height:1">${emoji}</span></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
}

function RecenterMap({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    if (!isFinite(lat) || !isFinite(lng)) return;
    try { map.setView([lat, lng], map.getZoom(), { animate: true }); } catch { /* invalid coords */ }
  }, [lat, lng, map]);
  return null;
}

function StarRating({ value, count }: { value: number | null | undefined; count: number }) {
  if (value == null) return <span className="text-xs text-slate-400">No ratings yet</span>;
  const num = Number(value);
  if (!isFinite(num)) return <span className="text-xs text-slate-400">No ratings yet</span>;
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} size={13} className={s <= Math.round(num) ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"} />
        ))}
      </div>
      <span className="text-sm font-semibold text-slate-800">{num.toFixed(1)}</span>
      <span className="text-xs text-slate-400">({count})</span>
    </div>
  );
}

function timeAgo(iso: string | null) {
  if (!iso) return "Unknown";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function VehicleIcon({ type }: { type: string }) {
  const cls = "shrink-0";
  if (type === "van") return <Truck size={14} className={cls} />;
  if (type === "car") return <Car size={14} className={cls} />;
  return <Bike size={14} className={cls} />;
}

type ChatMessage = {
  id: number;
  senderId: number;
  text: string;
  createdAt: string;
  senderName: string | null;
};

function DriverPanel({
  driver,
  onClose,
}: {
  driver: DriverPin;
  onClose: () => void;
}) {
  const [profile, setProfile] = useState<DriverProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [avatarImgError, setAvatarImgError] = useState(false);

  const [tab, setTab] = useState<"profile" | "chat">("profile");
  const [convId, setConvId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const myUserId = getMyUserId();

  useEffect(() => {
    setLoading(true);
    setProfile(null);
    setAvatarImgError(false);
    setTab("profile");
    setConvId(null);
    setMessages([]);
    fetch(`/api/drivers/${driver.userId}/public-profile`, { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) return;
        const d: unknown = await r.json();
        if (d && typeof d === "object" && "vehicleType" in d) {
          setProfile(d as DriverProfile);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [driver.userId]);

  async function loadMessages(cid: number) {
    try {
      const r = await fetch(`/api/conversations/${cid}/messages`, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json() as { items: ChatMessage[] };
      setMessages(d.items ?? []);
    } catch { /* silent */ }
  }

  async function openChat() {
    setTab("chat");
    if (convId !== null) return;
    setChatLoading(true);
    setChatError(null);
    try {
      const r = await fetch("/api/conversations/direct", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ userId: driver.userId }),
      });
      if (!r.ok) { setChatError("Could not open chat."); return; }
      const conv = await r.json() as { id: number };
      setConvId(conv.id);
      await loadMessages(conv.id);
    } catch {
      setChatError("Could not open chat.");
    } finally {
      setChatLoading(false);
    }
  }

  // Poll for new messages every 5s while chat tab is open
  useEffect(() => {
    if (tab === "chat" && convId !== null) {
      pollRef.current = setInterval(() => void loadMessages(convId), 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [tab, convId]);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (tab === "chat") {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, tab]);

  async function sendMessage() {
    if (!convId || !newMessage.trim() || sending) return;
    setSending(true);
    const text = newMessage.trim();
    setNewMessage("");
    try {
      const r = await fetch(`/api/conversations/${convId}/messages`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ text }),
      });
      if (!r.ok) { setNewMessage(text); return; }
      const msg = await r.json() as ChatMessage;
      setMessages((prev) => [...prev, msg]);
    } catch {
      setNewMessage(text);
    } finally {
      setSending(false);
    }
  }

  const data = profile ?? driver;

  return (
    <div className="absolute top-0 right-0 h-full w-80 bg-white shadow-2xl border-l border-slate-200 flex flex-col z-[500] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50 shrink-0">
        <span className="font-semibold text-slate-800 text-sm truncate mr-2">{data.name ?? "Driver"}</span>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors text-slate-500 shrink-0"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-slate-100 shrink-0">
        <button
          onClick={() => setTab("profile")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
            tab === "profile" ? "text-primary border-b-2 border-primary" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <User size={13} /> Profile
        </button>
        <button
          onClick={() => void openChat()}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
            tab === "chat" ? "text-primary border-b-2 border-primary" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <MessageCircle size={13} /> Chat
        </button>
      </div>

      {/* Profile tab */}
      {tab === "profile" && (
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center text-primary font-bold text-lg shrink-0">
                {data.avatarUrl && !avatarImgError
                  ? <img src={data.avatarUrl} alt="" className="w-full h-full object-cover" onError={() => setAvatarImgError(true)} />
                  : (data.name ?? "D").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-foreground">{data.name ?? "Driver"}</div>
                <div className="flex items-center gap-1.5 mt-0.5 text-slate-500 text-xs">
                  <VehicleIcon type={data.vehicleType} />
                  <span className="capitalize">{data.vehicleType}</span>
                  {profile?.town && <><span className="text-slate-300">·</span><span>{profile.town}</span></>}
                </div>
                <div className="mt-1.5">
                  <StarRating value={data.avgRating} count={data.ratingCount} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-400">
              <Clock size={11} />
              <span>Last seen {timeAgo(data.lastLocationAt)}</span>
              <span className="ml-1 w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
              <span className="text-emerald-600 font-medium">Available</span>
            </div>
          </div>

          {loading && <div className="p-4 text-sm text-slate-400 animate-pulse">Loading profile…</div>}

          {profile?.bio && (
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">About</p>
              <p className="text-sm text-slate-700 leading-relaxed">{profile.bio}</p>
            </div>
          )}

          {profile && (profile.serviceTowns ?? []).length > 0 && (
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Covers</p>
              <div className="flex flex-wrap gap-1.5">
                {(profile.serviceTowns ?? []).map((t) => (
                  <span key={t} className="flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full">
                    <MapPin size={10} />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {profile && (profile.recentReviews ?? []).length > 0 && (
            <div className="px-4 py-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Recent Reviews</p>
              <div className="space-y-3">
                {(profile.recentReviews ?? []).map((r, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-1.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={11} className={s <= r.rating ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"} />
                      ))}
                      <span className="text-xs text-slate-400 ml-1">{timeAgo(r.createdAt)}</span>
                    </div>
                    {r.comment && <p className="text-xs text-slate-700 leading-relaxed">"{r.comment}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {profile && (profile.recentReviews ?? []).length === 0 && !loading && (
            <div className="px-4 py-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Reviews</p>
              <p className="text-sm text-slate-400">No reviews yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Chat tab */}
      {tab === "chat" && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {chatLoading && (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 animate-pulse">
              Opening chat…
            </div>
          )}

          {chatError && !chatLoading && (
            <div className="flex-1 flex items-center justify-center p-4 text-sm text-red-500 text-center">
              {chatError}
            </div>
          )}

          {!chatLoading && !chatError && convId !== null && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
                {messages.length === 0 && (
                  <div className="text-center text-xs text-slate-400 mt-8">
                    No messages yet. Send one to start the conversation.
                  </div>
                )}
                {messages.map((msg) => {
                  const isMine = msg.senderId === myUserId;
                  return (
                    <div key={msg.id} className={`flex flex-col ${isMine ? "items-end" : "items-start"}`}>
                      {!isMine && msg.senderName && (
                        <span className="text-xs text-slate-400 mb-0.5 px-1">{msg.senderName}</span>
                      )}
                      <div
                        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed break-words ${
                          isMine
                            ? "bg-primary text-white rounded-br-sm"
                            : "bg-slate-100 text-slate-800 rounded-bl-sm"
                        }`}
                      >
                        {msg.text}
                      </div>
                      <span className="text-[10px] text-slate-400 mt-0.5 px-1">{timeAgo(msg.createdAt)}</span>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="shrink-0 border-t border-slate-100 p-2 flex gap-2 items-end">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void sendMessage(); }
                  }}
                  placeholder="Message driver…"
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder-slate-400 max-h-24 overflow-y-auto"
                />
                <button
                  onClick={() => void sendMessage()}
                  disabled={!newMessage.trim() || sending}
                  className="p-2 rounded-xl bg-primary text-white hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                >
                  <Send size={15} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AvailableDriversMap({
  mode,
  storeId,
  height = 520,
}: {
  mode: "admin" | "store";
  storeId?: number;
  height?: number;
}) {
  const [drivers, setDrivers] = useState<DriverPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState<DriverPin | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const AYRSHIRE_CENTER: [number, number] = [55.46, -4.63];

  const fetchDrivers = useCallback(async () => {
    try {
      const url = mode === "admin"
        ? "/api/admin/drivers/map"
        : `/api/order/stores/${storeId}/drivers/map`;
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json() as { items: DriverPin[] };
      setDrivers(d.items ?? []);
      setLastRefreshed(new Date());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [mode, storeId]);

  useEffect(() => {
    void fetchDrivers();
    refreshRef.current = setInterval(() => void fetchDrivers(), 30000);
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, [fetchDrivers]);

  const vehicleCounts = drivers.reduce((acc, d) => {
    acc[d.vehicleType] = (acc[d.vehicleType] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-slate-700">
            {drivers.length === 0 ? "No drivers online" : `${drivers.length} driver${drivers.length !== 1 ? "s" : ""} available`}
          </span>
          {drivers.length > 0 && (
            <div className="flex items-center gap-2">
              {vehicleCounts["car"] && (
                <span className="flex items-center gap-1 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 px-2 py-0.5 rounded-full">
                  <Car size={11} /> {vehicleCounts["car"]}
                </span>
              )}
              {vehicleCounts["van"] && (
                <span className="flex items-center gap-1 text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">
                  <Truck size={11} /> {vehicleCounts["van"]}
                </span>
              )}
              {vehicleCounts["bike"] && (
                <span className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                  <Bike size={11} /> {vehicleCounts["bike"]}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastRefreshed && (
            <span className="text-xs text-slate-400">Updated {timeAgo(lastRefreshed.toISOString())}</span>
          )}
          <button
            onClick={() => void fetchDrivers()}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm" style={{ height }}>
        <MapContainer
          center={AYRSHIRE_CENTER}
          zoom={11}
          style={{ width: "100%", height: "100%" }}
          scrollWheelZoom
          zoomControl
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />
          {selectedDriver && isFinite(selectedDriver.lastLat) && isFinite(selectedDriver.lastLng) && (
            <RecenterMap lat={selectedDriver.lastLat} lng={selectedDriver.lastLng} />
          )}
          {drivers.filter(d => isFinite(d.lastLat) && isFinite(d.lastLng)).map((d) => (
            <Marker
              key={d.userId}
              position={[d.lastLat, d.lastLng]}
              icon={makeDriverIcon(d.vehicleType ?? "bike", selectedDriver?.userId === d.userId)}
              eventHandlers={{
                click: () => setSelectedDriver(selectedDriver?.userId === d.userId ? null : d),
              }}
            />
          ))}
        </MapContainer>

        {/* Driver profile side panel */}
        {selectedDriver && (
          <MapErrorBoundary key={selectedDriver.userId}>
            <DriverPanel
              driver={selectedDriver}
              onClose={() => setSelectedDriver(null)}
            />
          </MapErrorBoundary>
        )}

        {/* Empty state overlay */}
        {!loading && drivers.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg px-6 py-4 text-center">
              <div className="text-3xl mb-2">🚲</div>
              <p className="font-semibold text-slate-700 text-sm">No drivers online right now</p>
              <p className="text-xs text-slate-400 mt-1">Drivers appear here when they're available and have shared their location</p>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="text-base">🚗</span> Car
        </span>
        <span className="flex items-center gap-1.5">
          <span className="text-base">🚐</span> Van
        </span>
        <span className="flex items-center gap-1.5">
          <span className="text-base">🚲</span> Bike
        </span>
        <span className="text-slate-300">·</span>
        <span>Click a marker to view driver profile and reviews</span>
        <span className="text-slate-300">·</span>
        <span>Refreshes every 30s</span>
      </div>
    </div>
  );
}
